/*
 * Copyright ish group pty ltd. All rights reserved. https://www.ish.com.au
 * No copying or use of this code is allowed without permission in writing from ish.
 */

import { ClassType, Contact, CourseClass, CourseClassPrice, Enrolment } from '@api';
import classnames from 'classnames';
import moment from 'moment';
import * as React from 'react';
import { OptionProps, components } from 'react-select';
import * as FormatUtils from '../../../../common/utils/FormatUtils';
import SelectField from '../../../../components/form-new/SelectField';
import { getFormInitialValues } from 'src/js/components/form/FieldFactory';
import { Formats } from 'src/js/constants/Formats';
import { CourseClassService } from 'src/js/web/services/CourseClassService';
import { ClassHasCommenced } from '../Messages';
import CustomFieldsForm from './CustomFieldsForm';
import { ItemWrapper } from './ItemWrapper';

export interface Props {
  contact: Contact;
  enrolment: Enrolment;
  courseClass: CourseClass;
  onChange?: (item, contact) => void;
  onChangeClass?: (classId: string, newClasses: CourseClass[]) => void;
  onChangeFields?: (form, type) => any;
  readonly?: boolean;
  onAddNewClasses?: any;
}

const EnrolmentComp = (
  {
    enrolment, courseClass, contact, onChange, onChangeClass, onChangeFields, readonly, onAddNewClasses
  }:Props) => {

  const divClass = classnames('row', 'enrolmentItem', { disabled: !enrolment.selected });
  const name = `enrolment-${contact?.id}-${enrolment.classId}`;
  const title = `${courseClass.course.name}`;

  let warning = enrolment.warnings && enrolment.warnings.length ? enrolment.warnings[0] : null;
  const error = enrolment.warnings && enrolment.errors.length ? enrolment.errors[0] : null;
  if (!warning && courseClass.start && moment(courseClass.start).isBefore(moment())) {
    warning = ClassHasCommenced;
  }

  return (
    <div className={divClass}>
      <ItemWrapper
        title={title}
        name={name}
        error={error}
        warning={warning}
        selected={!error && enrolment.selected}
        item={enrolment}
        contact={contact}
        onChange={onChange}
        readonly={readonly}
      >
        <ClassDetails
          courseClass={courseClass}
          onChangeClass={onChangeClass}
          onAddNewClasses={onAddNewClasses}
          readonly={readonly}
        />
      </ItemWrapper>
      {!error && enrolment.selected && courseClass.price && <ClassPrice enrolment={enrolment} />}

      {!readonly && (
        <CustomFieldsForm
          headings={enrolment.fieldHeadings}
          classId={enrolment.classId}
          selected={enrolment.selected}
          form={`${enrolment.contactId}-${enrolment.classId}`}
          onSubmit={() => undefined}
          initialValues={getFormInitialValues(enrolment.fieldHeadings)}
          onUpdate={(form) => onChangeFields(form, 'enrolments')}
        />
      )}
    </div>
  );
}

const ClassPrice = (props): any => {
  const { enrolment } = props;
  const price: CourseClassPrice = enrolment.price || {};

  const fee = price.feeOverriden ? Number(price.feeOverriden).toFixed(2) : Number(price.fee).toFixed(2);
  const discountedFee = price.appliedDiscount ? Number(price.appliedDiscount.discountedFee).toFixed(2) : null;
  const discount = price.appliedDiscount ? Number(price.appliedDiscount.discountValue).toFixed(2) : null;

  const feeStyle = price.appliedDiscount ? { textDecoration: 'line-through' } : null;

  return (
    <div className="col-8 col-xs-8 col-md-8 alignright">
      {(fee || Number(fee) === 0)
        && (
        <div className="text-right">
          <span className="fee-full fullPrice" style={feeStyle}>
            $
            {fee}
          </span>
        </div>
        )}

      {discountedFee !== null
        && (
        <div className="text-right">
          <span className="fee-discounted discountedPrice">
            $
            {discountedFee}
          </span>
        </div>
        )}

      {discount !== null
        && (
        <span style={{ display: 'none' }} className="discount">
          $
          {discount}
        </span>
        )}
    </div>
  );
};

interface ClassDetailsProps {
  courseClass: CourseClass;
  onChangeClass?: (classId: string, newClasses: CourseClass[]) => void;
  onAddNewClasses: any;
  readonly?: boolean;
}

const ClassDetailsItem = (classItem: CourseClass) => {
  const start: string = FormatUtils.formatDate(classItem.start, Formats.ClassDateFormat, classItem.timezone);
  const end: string = FormatUtils.formatDate(classItem.end, Formats.ClassDateFormat, classItem.timezone);

  return <div>
    {classItem.room && <span>{`${classItem.room.site.name} » `}</span>}
    {classItem.type === ClassType['Distant Learning'] && <span className="started">Self paced</span>}
    {start && end
      && (
        <span>
        <span className="started">{start}</span>
          &nbsp;-&nbsp;
          <span className="ended">{end}</span>
      </span>
      )}
  </div>
}

const ClassDetailsLabel = (option: OptionProps<CourseClass>) => <components.Option {...option}>
  <ClassDetailsItem {...option.data}/>
</components.Option>;

const ClassDetailsValue = (option: OptionProps<CourseClass>) => <components.SingleValue {...option}>
  <ClassDetailsItem {...option.data}/>
</components.SingleValue>;

class ClassDetails extends React.PureComponent<ClassDetailsProps, any> {
  state = {
    availableClasses: []
  };

  componentDidMount() {
    const { courseClass } = this.props;

    CourseClassService.getAvailableClasses(courseClass.course.id).then((classes) => {
      this.setState({
        availableClasses: Array.isArray(classes) ? classes.filter((c) => c.hasAvailablePlaces) : []
      });
    });
  }

  render() {
    const { courseClass, onChangeClass, readonly } = this.props;
    const { availableClasses } = this.state;

    return courseClass.id && !readonly ? (
      <SelectField
        returnType="object"
        searchable={false}
        valueKey="id"
        labelKey="id"
        input={{
          onChange: (result) => {
            if (!result || result.id === courseClass.id) {
              return;
            }
            onChangeClass(result.id, availableClasses);
          },
          value: courseClass
        } as any}
        meta={{} as any}
        optionRenderer={ClassDetailsLabel}
        valueRenderer={ClassDetailsValue}
        loadOptions={() => Promise.resolve(availableClasses)}
      />
    ) : courseClass.id;
  }
}

export default EnrolmentComp;